import React from "react";
import { useNavigate } from "react-router-dom";
import "./Services.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBullseye,
  faLaptopCode,
  faMagnifyingGlassChart,
  faEnvelopeOpenText,
  faChartLine,
  faUserGroup,
} from "@fortawesome/free-solid-svg-icons";

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: "growth-blueprint",
      icon: faChartLine,
      badge: "Good starting point",
      title: "Full Check-Up of Your Online Presence",
      outcome: "See clearly what is helping your business online and what is holding it back.",
      desc:
        "We look at your website, ads, Google listing and social pages like a doctor’s check-up. Then we tell you, in simple words, what is okay, what is weak and what to fix first so your online presence actually brings more business.",
      goodFor: [
        "Old or confusing websites",
        "Businesses getting a few enquiries but want more",
        "Owners who feel “something is wrong” but don’t know what",
      ],
    },
    {
      id: "acquisition-sprints",
      icon: faBullseye,
      badge: "More enquiries",
      title: "Google & Facebook / Instagram Ads for Leads",
      outcome: "Turn ad money into real calls, messages and form enquiries.",
      desc:
        "We set up and manage simple ads on Google and Facebook/Instagram. Clear offers, the right people seeing your ads, and matching pages on your website — so more people call you or fill your form instead of just clicking and leaving.",
      goodFor: [
        "Local service businesses (doctors, salons, repair etc.)",
        "Consultants, coaches and B2B services",
        "Businesses already running ads but not happy with results",
      ],
    },
    {
      id: "site-funnels",
      icon: faLaptopCode,
      badge: "Website & pages",
      title: "Simple, Lead-Focused Website & Pages",
      outcome: "Make it easy for visitors to understand you and contact you.",
      desc:
        "We create or update your website so it looks clean, loads fast and works well on mobile. Clear sections: who you are, what you offer, prices or packages, proof of work and big buttons like “Call Now”, “WhatsApp Us” or “Book a Slot”.",
      goodFor: [
        "Websites that look old or hard to use",
        "Getting visitors but very few calls or enquiries",
        "Launching a new service, product or city",
      ],
    },
    {
      id: "retention-crm",
      icon: faEnvelopeOpenText,
      badge: "Stay in touch",
      title: "Follow-Ups on Email & WhatsApp",
      outcome: "Remind people you exist so they come back again.",
      desc:
        "We set up simple follow-up messages for people who contacted you, visited your site or bought from you once. Friendly emails or WhatsApp messages with tips, updates and offers — so they remember you when they need that service again.",
      goodFor: [
        "Businesses with repeat customers",
        "Trainers, teachers and course creators",
        "Service brands that want more repeat work",
      ],
    },
    {
      id: "always-on-seo",
      icon: faMagnifyingGlassChart,
      badge: "Be found",
      title: "Help People Find You on Google",
      outcome: "Show up better when people search for your type of business.",
      desc:
        "We fix important basics so Google understands your website: headings, text, speed and page structure. Then we plan simple articles and service pages around the real phrases your customers type, so they find you instead of a competitor.",
      goodFor: [
        "Local shops and service providers",
        "Specialised B2B businesses",
        "Owners who want steady results over time, not quick tricks",
      ],
    },
    {
      id: "embedded-pod",
      icon: faUserGroup,
      badge: "Ongoing help",
      title: "Part-Time Online Marketing Team",
      outcome: "Have a small expert team treating your business like their own.",
      desc:
        "Instead of hiring many people, you get one small team that regularly checks your website, ads and messages. Every month we plan what to improve, make changes for you and send a simple report you can understand in 5–10 minutes.",
      goodFor: [
        "Busy founders and owners",
        "Small teams without a marketing person",
        "Brands growing into new areas or countries",
      ],
    },
  ];

  const open = (id) => navigate(`/services/${id}`);
  const onKey = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open(id);
    }
  };

  return (
    <section className="svc2" aria-label="Online marketing services">
      <div className="svc2-wrap">
        {/* Header */}
        <header className="svc2-head">
          <p className="svc2-kicker">Services & ways of working</p>
          <h1>
            Simple online marketing help,
            <span className="svc2-highlight"> explained in normal language.</span>
          </h1>
          <p className="svc2-sub">
            We help small businesses get more calls, messages and sales from the internet.
            You can start with just your website, ads, Google presence or follow-ups — and
            add more later when you’re ready.
          </p>

          <div className="svc2-pills" aria-hidden="true">
            <span className="pill">Fix or improve your website</span>
            <span className="pill">Run Google & Facebook / Instagram ads</span>
            <span className="pill">Follow up with old and warm leads</span>
            <span className="pill">Have a part-time marketing team</span>
          </div>
        </header>

        {/* Layout: left “how to choose”, right grid */}
        <div className="svc2-layout">
          {/* Left sidebar explainer */}
          <aside className="svc2-sidebar" aria-label="How to choose a starting point">
            <h2>Not sure which service to choose?</h2>
            <p>
              Most clients start with just one main problem. Read these lines and see
              which one sounds like your situation right now.
            </p>

            <ul className="svc2-start-list">
              <li>
                <span className="dot" />
                <div>
                  <strong>“Our website looks old and doesn’t bring enquiries.”</strong>
                  <p>
                    Start with the <em>Full Check-Up</em> and an important page refresh.
                  </p>
                </div>
              </li>
              <li>
                <span className="dot" />
                <div>
                  <strong>“We are paying for ads but not getting enough leads.”</strong>
                  <p>
                    Start with <em>Google & Facebook / Instagram Ads</em> and a strong page.
                  </p>
                </div>
              </li>
              <li>
                <span className="dot" />
                <div>
                  <strong>“People buy once and then forget about us.”</strong>
                  <p>
                    Start with <em>Follow-Ups on Email & WhatsApp</em> so they remember you.
                  </p>
                </div>
              </li>
            </ul>

            <button
              className="svc2-mini-btn"
              onClick={() => navigate("/contact")}
              aria-label="Talk through your options"
            >
              Talk through options in a quick 20–30 min call
            </button>
          </aside>

          {/* Services grid */}
          <div className="svc2-grid" role="list">
            {services.map((s) => (
              <article
                key={s.id}
                role="listitem"
                className="svc2-card"
                tabIndex={0}
                onClick={() => open(s.id)}
                onKeyDown={(e) => onKey(e, s.id)}
                aria-label={`${s.title} — ${s.desc}`}
              >
                <div className="svc2-top">
                  <div className="svc2-icon">
                    <FontAwesomeIcon icon={s.icon} />
                  </div>
                  <span className="svc2-label">{s.badge}</span>
                </div>

                <h3 className="svc2-title">{s.title}</h3>
                <p className="svc2-outcome">
                  <span>Outcome:</span> {s.outcome}
                </p>
                <p className="svc2-desc">{s.desc}</p>

                <div className="svc2-tags">
                  {s.goodFor.map((t) => (
                    <span key={t} className="tag">
                      {t}
                    </span>
                  ))}
                </div>

                <div className="svc2-cta" aria-hidden="true">
                  View what’s included & simple examples →
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Process strip */}
        <div className="svc2-process" aria-label="How projects typically run">
          <h2>How working together usually looks</h2>
          <div className="svc2-process-steps">
            <div className="proc-step">
              <div className="proc-step-num">1</div>
              <div className="proc-step-body">
                <h3>Short call to understand your business</h3>
                <p>
                  We ask basic, direct questions: what you sell, where your customers come
                  from now, and what “success” would mean for you. If it’s not a good fit,
                  we’ll say so openly.
                </p>
              </div>
            </div>
            <div className="proc-step">
              <div className="proc-step-num">2</div>
              <div className="proc-step-body">
                <h3>Simple plan and first tasks</h3>
                <p>
                  We send a small, clear plan for the next 4–8 weeks: what we’ll do, what we
                  need from you, and what we will track. No big documents — just plain
                  to-do items in order.
                </p>
              </div>
            </div>
            <div className="proc-step">
              <div className="proc-step-num">3</div>
              <div className="proc-step-body">
                <h3>We set up, launch and improve</h3>
                <p>
                  We do the work, send quick updates (often short videos or messages) and
                  then improve things based on hard numbers: more calls, enquiries and sales,
                  not just more “visits”.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA row */}
        <div className="svc2-cta-row">
          <button
            className="svc2-btn svc2-btn-primary"
            onClick={() => navigate("/contact")}
            aria-label="Outline your goals and pick a starting service"
          >
            Tell us your goals & pick a starting service
          </button>
          <button
            className="svc2-btn svc2-btn-ghost"
            onClick={() => navigate("/process")}
            aria-label="See the full process and FAQs"
          >
            See how our work process looks
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
